import React, { useState, useEffect } from "react";
import { DataTable } from "@/components/ui/data-table";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { Button } from "@/components/ui/button";
import { Plus, Eye, Edit } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { SpinAWheelApi, SpinWheelConfig } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { CreateSpinWheelForm } from "./CreateSpinWheelForm";
import { Badge } from "@/components/ui/badge";

interface SpinWheel {
  id: number;
  spinName: string;
  startDate: string;
  endDate: string;
  noOfSlots: number;
  spinPerMember: number;
  bonusValidityInDay: number;
  nonWinningSlots: number;
}

export function GamificationAllSpinWheels() {
  const [spinWheels, setSpinWheels] = useState<SpinWheel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSpinWheel, setSelectedSpinWheel] =
    useState<SpinWheelConfig | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchSpinWheels();
  }, []);

  const fetchSpinWheels = async () => {
    setIsLoading(true);
    try {
      const response = await SpinAWheelApi.getAllSpinAwheelConfig({});

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        // Map API response to SpinWheel interface
        const mappedSpinWheels: SpinWheel[] = (
          response.getSpinAwheelConfig || []
        ).map((config: any) => ({
          id: config.id || 0,
          spinName: config.spinName || "",
          startDate: config.startDate || "",
          endDate: config.endDate || "",
          noOfSlots: config.noOfSlots || 0,
          spinPerMember: config.spinPerMember || 0,
          bonusValidityInDay: config.bonusValidityInDay || 0,
          nonWinningSlots: config.nonWinningSlots || 0,
        }));
        setSpinWheels(mappedSpinWheels);
      }
    } catch (error: any) {
      console.error("Error fetching spin wheels:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch spin wheels",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return format(date, "MMM dd, yyyy");
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString || dateString === "0001-01-01T00:00:00") return "-";
    try {
      const date = new Date(dateString);
      return format(date, "MMM dd, yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  const handleView = async (wheel: SpinWheel) => {
    try {
      // Fetch full spin wheel config with slots
      const response = await SpinAWheelApi.getAllSpinAwheelConfig({});
      if (response.getSpinAwheelConfig) {
        const fullConfig = response.getSpinAwheelConfig.find(
          (config: any) => config.id === wheel.id,
        );
        if (fullConfig) {
          // Map API response to SpinWheelConfig format
          const mappedConfig: SpinWheelConfig = {
            id: fullConfig.id || wheel.id,
            spinName: fullConfig.spinName || wheel.spinName,
            startDate: fullConfig.startDate || wheel.startDate,
            endDate: fullConfig.endDate || wheel.endDate,
            noOfSlots: fullConfig.noOfSlots || wheel.noOfSlots,
            spinPerMember: fullConfig.spinPerMember || wheel.spinPerMember,
            bonusValidityInDay:
              fullConfig.bonusValidityInDay || wheel.bonusValidityInDay,
            nonWinningSlots:
              fullConfig.nonWinningSlots || wheel.nonWinningSlots,
            useDefault: fullConfig.useDefault,
            spinAWheelSlots:
              fullConfig.spinAWheelSlots || fullConfig.listWheelSlots || [],
          };
          setSelectedSpinWheel(mappedConfig);
          setIsViewModalOpen(true);
        } else {
          // Fallback: use basic wheel data
          setSelectedSpinWheel({
            id: wheel.id,
            spinName: wheel.spinName,
            startDate: wheel.startDate,
            endDate: wheel.endDate,
            noOfSlots: wheel.noOfSlots,
            spinPerMember: wheel.spinPerMember,
            bonusValidityInDay: wheel.bonusValidityInDay,
            nonWinningSlots: wheel.nonWinningSlots,
            useDefault: 0,
            spinAWheelSlots: [],
          } as SpinWheelConfig);
          setIsViewModalOpen(true);
        }
      }
    } catch (error: any) {
      console.error("Error fetching spin wheel details:", error);
      // Fallback: use basic wheel data
      setSelectedSpinWheel({
        id: wheel.id,
        spinName: wheel.spinName,
        startDate: wheel.startDate,
        endDate: wheel.endDate,
        noOfSlots: wheel.noOfSlots,
        spinPerMember: wheel.spinPerMember,
        bonusValidityInDay: wheel.bonusValidityInDay,
        nonWinningSlots: wheel.nonWinningSlots,
        useDefault: 0,
        spinAWheelSlots: [],
      } as SpinWheelConfig);
      setIsViewModalOpen(true);
    }
  };

  const handleEdit = (wheel: SpinWheelConfig) => {
    setSelectedSpinWheel(wheel);
    setIsViewModalOpen(false);
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-800">All Spin Wheels</h2>
          <p className="text-sm text-muted-foreground">
            View and manage all spin wheel configurations
          </p>
        </div>
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 bg-orange-600 text-white hover:bg-orange-700"
        >
          <Plus className="h-4 w-4" />
          Create Spin Wheel
        </Button>
      </div>

      {isLoading ? (
        <CouponTableSkeleton rowCount={5} />
      ) : (
        <DataTable
          title=""
          data={spinWheels}
          cellClassName="py-6 text-gray-700"
          columns={[
            {
              header: "Spin Wheel Name",
              accessorKey: "spinName" as keyof SpinWheel,
            },
            {
              header: "Start Date",
              accessorKey: "startDate" as keyof SpinWheel,
              cell: (row: SpinWheel) => formatDate(row.startDate),
            },
            {
              header: "End Date",
              accessorKey: "endDate" as keyof SpinWheel,
              cell: (row: SpinWheel) => formatDate(row.endDate),
            },
            {
              header: "No of Slots",
              accessorKey: "noOfSlots" as keyof SpinWheel,
              cell: (row: SpinWheel) => row.noOfSlots.toString(),
            },
            {
              header: "Spin per Member",
              accessorKey: "spinPerMember" as keyof SpinWheel,
              cell: (row: SpinWheel) => `${row.spinPerMember} per day`,
            },
            {
              header: "Bonus Validity (Days)",
              accessorKey: "bonusValidityInDay" as keyof SpinWheel,
              cell: (row: SpinWheel) => `${row.bonusValidityInDay} days`,
            },
            {
              header: "Action",
              id: "action",
              cell: (row: SpinWheel) => (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleView(row)}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View
                </Button>
              ),
            },
          ]}
        />
      )}

      {/* View Spin Wheel Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Spin Wheel Details</DialogTitle>
          </DialogHeader>
          {selectedSpinWheel && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    ID
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSpinWheel.id}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Spin Wheel Name
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSpinWheel.spinName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Start Date
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate(selectedSpinWheel.startDate)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    End Date
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate(selectedSpinWheel.endDate)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    No of Slots
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSpinWheel.noOfSlots}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Spin per Member
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSpinWheel.spinPerMember} per day
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Bonus Validity (Days)
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSpinWheel.bonusValidityInDay} days
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Non Winning Slots
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSpinWheel.nonWinningSlots}
                  </p>
                </div>
              </div>
              {selectedSpinWheel.spinAWheelSlots &&
                selectedSpinWheel.spinAWheelSlots.length > 0 && (
                  <div className="mt-4">
                    <label className="text-sm font-medium text-gray-500 mb-2 block">
                      Slot Details
                    </label>
                    <div className="space-y-2">
                      {selectedSpinWheel.spinAWheelSlots.map((slot, index) => (
                        <div key={index} className="p-3 border rounded-md">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">
                              Slot {index + 1}:{" "}
                              {slot.slotName || `Slot ${index + 1}`}
                            </span>
                            <Badge variant="default" className="ml-2">
                              {slot.points} points
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsViewModalOpen(false)}
            >
              Close
            </Button>
            {selectedSpinWheel && (
              <Button
                type="button"
                className="flex items-center gap-2 bg-orange-600 text-white hover:bg-orange-700"
                onClick={() => handleEdit(selectedSpinWheel)}
              >
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Spin Wheel Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Spin Wheel</DialogTitle>
          </DialogHeader>
          <CreateSpinWheelForm
            initialData={selectedSpinWheel}
            isEditMode={true}
            onSuccess={() => {
              setIsEditModalOpen(false);
              setSelectedSpinWheel(null);
              // Refresh spin wheel list after updating
              fetchSpinWheels();
            }}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedSpinWheel(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Create Spin Wheel Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Spin Wheel</DialogTitle>
          </DialogHeader>
          <CreateSpinWheelForm
            onSuccess={() => {
              setIsCreateModalOpen(false);
              // Refresh spin wheel list after creating
              fetchSpinWheels();
            }}
            onCancel={() => setIsCreateModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
